// 내비게이션 단일 소스 (eventTypes.js / taskStatus.js 패턴)
//
// BottomNav, SideNav, TopBar 의 제목 해석이 모두 이 배열을 읽는다.
// 탭을 추가/삭제할 때 손대야 할 곳은 여기 하나다.
//
// badge 는 값이 아니라 **스토어 게터 이름**을 담는다.
// 값으로 넣으면 모듈 로드 시점에 한 번 평가되고 끝나서, 지금 Navigation.vue 가
// onMounted 에서만 배지를 갱신하던 문제(세션 내내 숫자가 얼어붙음)가 재현된다.
//
// show 도 마찬가지로 게터 이름이다. 없으면 항상 표시.
//
// ── surface: 어느 내비게이션에 나오는가 ──
//   'both'  좌측 레일과 하단 탭바 양쪽
//   'rail'  좌측 레일에만 (데스크톱은 자리가 남는다)
//   'tabs'  하단 탭바에만 (모바일 전용 관문)
//
// 결재함이 'rail' 인 이유: 하단 탭은 5칸이고, 12명 중 9명은 결재할 게 없다.
// 빈 탭은 5칸짜리 바에서 영구 사표다. 모바일에서는 홈의 결재 카드와
// 보고 탭을 통해 들어간다. 반대로 레일은 세로라 한 줄 더 놓을 자리가 있다.
//
// '더보기' 가 'tabs' 인 이유: 데스크톱에서 프로필·조직도·장소 관리는
// 레일 하단의 계정 메뉴에 들어간다. 레일에 '더보기' 를 두면 메뉴가 두 겹이 된다.

export const NAV_ITEMS = [
  { key: 'today', to: '/today', label: '홈', icon: 'Sunny', surface: 'both' },
  { key: 'calendar', to: '/calendar', label: '캘린더', icon: 'Calendar', surface: 'both' },
  { key: 'tasks', to: '/tasks', label: '업무', icon: 'List', badge: 'taskBadgeCount', surface: 'both' },
  // 배지가 '내가 결재할 문서' 수인 이유: 결재함이 rail 전용이라 폰에는 탭이 없고,
  // 위 주석대로 모바일 진입 경로가 이 탭이다. 대기 건수가 어디에도 안 보이면
  // 결재자는 앱을 열 이유를 얻지 못한다. 탭 라벨을 '결재' 로 바꾸지는 않는다 —
  // 대기가 상시인 사람에게서 보고 작성 경로를 영구히 뺏게 된다.
  { key: 'reports', to: '/reports', label: '업무보고', icon: 'Document', badge: 'pendingApprovalCount', show: 'canReport', surface: 'both' },
  {
    key: 'approvals',
    to: '/approvals',
    label: '결재함',
    icon: 'Stamp',
    badge: 'pendingApprovalCount',
    show: 'canReport',
    surface: 'rail'
  },
  // 영업이 'rail' 인 이유: 하단 탭 5칸이 이미 찼고, 12명 중 영업을 매일 여는 사람은 일부다.
  // 폰에서는 더보기 맨 위 행으로 들어간다. 매일 쓰는 사람은 더보기의 [하단 탭에 영업 두기] 로
  // '업무보고' 자리와 바꾼다(utils/navPrefs.js · visibleNavItems 의 salesTab).
  { key: 'sales', to: '/sales', label: '영업', icon: 'Shop', surface: 'rail' },
  // 인사관리 — 인사 권한이 있는 사람에게만(canHr). 영업과 같은 이유로 레일 전용, 폰은 더보기의 큰 행으로 들어간다.
  { key: 'hr', to: '/hr', label: '인사', icon: 'Postcard', show: 'canHr', surface: 'rail' },
  // 회의 — 거래처 미팅 · 관리자 회의록. 모두 쓰지만 매일 여는 화면은 아니라 영업과 같이 레일 전용, 폰은 더보기의 큰 행.
  { key: 'meetings', to: '/meetings', label: '회의', icon: 'Notebook', surface: 'rail' },
  { key: 'more', to: '/more', label: '더보기', icon: 'Grid', surface: 'tabs' }
]

/**
 * 스토어 게터를 참조해 실제로 보여줄 항목만 남긴다.
 * @param {object} getters 스토어 게터
 * @param {'rail'|'tabs'} surface 어느 내비게이션인지
 */
export function visibleNavItems(getters, surface = 'tabs', { salesTab = false } = {}) {
  return NAV_ITEMS.filter((item) => {
    // 하단 탭에 영업을 두기로 한 사람(navPrefs.salesInTabs): 업무보고 자리에 영업
    if (surface === 'tabs' && salesTab) {
      if (item.key === 'sales') return true
      if (item.key === 'reports') return false
    }
    if (item.surface !== 'both' && item.surface !== surface) return false
    return !item.show || !!getters[item.show]
  })
}

/** 배지 숫자. 0 이거나 게터가 없으면 0 을 반환해 호출부에서 v-if 로 걸러낼 수 있게 한다. */
export function navBadgeCount(item, getters) {
  if (!item.badge) return 0
  return Number(getters[item.badge] || 0)
}

/** 배지를 포함한 접근성 라벨. 예: "업무, 지연 3건" */
export function navAriaLabel(item, count) {
  if (!count) return item.label
  const suffix = item.key === 'tasks' ? '지연' : '대기'
  return `${item.label}, ${suffix} ${count}건`
}

/** 탭 라벨은 좁은 하단 탭바용으로 짧게 쓴다. 레일은 전체 라벨. */
export function navShortLabel(item) {
  return item.key === 'reports' ? '보고' : item.label
}
